import { useMemo } from "react";

import useConfig from "./useConfig";

export default function useFileValidation() {
    const { getConfig, loading: configLoading } = useConfig();

    const fileConfig = useMemo(() => {
        if(configLoading) return null;

        // Get file extensions from config
        const documentExtensions = getConfig(
            "FILE_EXTENSIONS_DOCUMENTS",
        )?.split(",") || [".doc", ".docx", ".xlsx", ".pdf", ".pptx", ".xls"];

        const imageExtensions = getConfig("FILE_EXTENSIONS_IMAGES")?.split(
            ",",
        ) || [".gif", ".png", ".jpeg", ".jpg"];

        const audioExtensions = getConfig("FILE_EXTENSIONS_AUDIO")?.split(
            ",",
        ) || [".mp4", ".wav", ".mp3"];

        // Get file size limits from config (in bytes)
        const documentSizeLimit =
            parseInt(getConfig("FILE_SIZE_LIMIT_DOCUMENTS")) || 26214400;
        const imageSizeLimit =
            parseInt(getConfig("FILE_SIZE_LIMIT_IMAGES")) || 15728640;
        const audioSizeLimit =
            parseInt(getConfig("FILE_SIZE_LIMIT_AUDIO")) || 26214400;

        // MIME type mappings for additional validation
        const documentMimeTypes = [
            "application/pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "application/vnd.ms-excel",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "application/vnd.ms-powerpoint",
            "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        ];

        const imageMimeTypes = [
            "image/gif",
            "image/png",
            "image/jpeg",
            "image/jpg",
        ];

        const audioMimeTypes = ["audio/mpeg", "audio/wav", "video/mp4"];

        return {
            documents: {
                extensions: documentExtensions,
                mimeTypes: documentMimeTypes,
                maxSize: documentSizeLimit,
                maxSizeMB: Math.round(documentSizeLimit / (1024 * 1024)),
                label: "DOC, DOCX, XLSX, PDF, PPTX, and XLS document",
            },
            images: {
                extensions: imageExtensions,
                mimeTypes: imageMimeTypes,
                maxSize: imageSizeLimit,
                maxSizeMB: Math.round(imageSizeLimit / (1024 * 1024)),
                label: "GIF, PNG, JPEG, and JPG image",
            },
            audio: {
                extensions: audioExtensions,
                mimeTypes: audioMimeTypes,
                maxSize: audioSizeLimit,
                maxSizeMB: Math.round(audioSizeLimit / (1024 * 1024)),
                label: "MP4, WAV, and MP3 audio/video",
            },
        };
    }, [getConfig, configLoading]);

    const validateFile = (file, fileType) => {
        const errors = [];

        if(!file) {
            errors.push("Please select a file.");

            return { isValid: false, errors };
        }

        if(!fileConfig || configLoading) {
            errors.push("Configuration is loading. Please try again.");

            return { isValid: false, errors };
        }

        const config = fileConfig[fileType];

        if(!config) {
            errors.push("Invalid file type specified.");

            return { isValid: false, errors };
        }

        // Check file type (both extension and MIME type)
        const hasValidExtension = config.extensions.some((ext) =>
            file.name.toLowerCase().endsWith(ext.toLowerCase()),);

        const hasValidMimeType = config.mimeTypes.some(
            (mimeType) =>
                file.type === mimeType || file.type.startsWith(mimeType),
        );

        if(!hasValidExtension || !hasValidMimeType) {
            const extensionsList = config.extensions.join(", ");

            errors.push(
                `Invalid file type. Only ${config.label} files are supported (${extensionsList})`,
            );
        }

        // Check file size
        if(file.size > config.maxSize) {
            errors.push(
                `File size too large. Maximum size allowed is ${config.maxSizeMB}MB`,
            );
        }

        return {
            isValid: errors.length === 0,
            errors,
        };
    };

    const getFileTypeConfig = (fileType) => {
        return fileConfig?.[fileType] || null;
    };

    const getAllowedTypes = (fileType) => {
        const config = fileConfig?.[fileType];
        if(!config) return [];

        return [...config.extensions, ...config.mimeTypes];
    };

    const getAcceptString = (fileType) => {
        const config = fileConfig?.[fileType];

        if(!config) return "";

        return [...config.extensions, ...config.mimeTypes].join(",");
    };

    return {
        validateFile,
        getFileTypeConfig,
        getAllowedTypes,
        getAcceptString,
        fileConfig,
        loading: configLoading,
    };
}
